import { useMemo } from "react";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { useMantineColorScheme, Button, Group } from "@mantine/core";
import { exportTableToCsv } from "../../utils/csvExport";
import { useTranslation } from "react-i18next";
import { formatCompactNumber, formatEnUsValue } from "../../utils/helpers";

const CashFlowTable = ({
  resultSimulationData,
  isDiscounted = false,
  isCashflowCumulative = false,
}) => {
  const { t } = useTranslation();

  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";
  const backgroundColor = isLightMode ? "#FFFFFF" : "var(--se-background-alternative)";
  const pinnedColumnBg = isLightMode ? "#E6F3FF" : "rgba(66, 180, 230, 0.24)";
  const textCellSx = {
    fontWeight: "normal",
    fontSize: "14px",
    fontFamily: "Nunito",
    backgroundColor,
  };

  const hasAssetCashflow = Array.isArray(resultSimulationData?.assetCashflow?.assets);
  const tableData = useMemo(() => {
    if (hasAssetCashflow) {
      const valueKey = isCashflowCumulative
        ? isDiscounted
          ? "cumulativeDiscountedValues"
          : "cumulativeNominalValues"
        : isDiscounted
          ? "relativeDiscountedValues"
          : "relativeNominalValues";
      const assets = resultSimulationData.assetCashflow.assets;
      const yearCount = Math.max(
        0,
        ...assets.flatMap((asset) =>
          (asset?.cashFlows || []).map((cashFlow) =>
            Array.isArray(cashFlow?.[valueKey]) ? cashFlow[valueKey].length : 0
          )
        )
      );
      const rows = [];
      const yearlyTotals = Array(yearCount).fill(0);

      assets.forEach((asset) => {
        rows.push({
          rowLabel: asset?.assetType || "",
          rowType: "asset",
        });

        (asset?.cashFlows || []).forEach((cashFlow) => {
          const values = Array.from({ length: yearCount }, (_, yearIndex) => {
            const value = Number(cashFlow?.[valueKey]?.[yearIndex] ?? 0);
            const numericValue = Number.isFinite(value) ? value : 0;
            yearlyTotals[yearIndex] += numericValue;
            return numericValue;
          });
          rows.push({
            rowLabel: cashFlow?.costType || "",
            rowType: "cashflow",
            ...Object.fromEntries(values.map((value, index) => [`year${index}`, value])),
          });
        });
      });

      rows.push({
        rowLabel: t("common.total", { defaultValue: "Total" }),
        rowType: "total",
        ...Object.fromEntries(yearlyTotals.map((value, index) => [`year${index}`, value])),
      });

      return rows;
    }

    const key = isDiscounted ? 'discountedValue' : 'nominalValue';
    return resultSimulationData?.cashflow?.map((project, index) => ({
      year: index + 1,
      ...(project?.[key] || {}),
      rowTotal: Object.entries(project?.[key] || {}).reduce((sum, [field, value]) => {
        if (field === "year") return sum;
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? sum + numericValue : sum;
      }, 0),
    }));
  }, [hasAssetCashflow, isCashflowCumulative, isDiscounted, resultSimulationData, t]);

  const renderFormattedCell = (cell, useCompact = true) => {
    const rawValue = cell.getValue();
    const numericValue = Number(rawValue);

    if (!Number.isFinite(numericValue)) {
      return rawValue ?? "-";
    }

    if (!useCompact) {
      return rawValue;
    }

    const displayValue = formatCompactNumber(numericValue);
    const hoverValue = formatEnUsValue(numericValue);

    return <span title={hoverValue}>{displayValue}</span>;
  };

  const columns = useMemo(() => {
    if (hasAssetCashflow) {
      const yearCount = Math.max(
        0,
        ...tableData.map((row) =>
          Object.keys(row).filter((key) => /^year\d+$/.test(key)).length
        )
      );
      return [
        {
          accessorKey: "rowLabel",
          header: "Row Labels",
          size: 220,
          Cell: ({ cell, row }) => (
            <span style={{ fontWeight: row.original.rowType === "asset" || row.original.rowType === "total" ? 700 : 400, paddingLeft: row.original.rowType === "cashflow" ? 20 : 0 }}>
              {cell.getValue()}
            </span>
          ),
        },
        ...Array.from({ length: yearCount }, (_, index) => ({
          accessorKey: `year${index}`,
          header: `${t("result.cashFlow.year", { defaultValue: "Year" })} ${index}`,
          size: 110,
          Cell: ({ cell }) => renderFormattedCell(cell),
        })),
      ];
    }

    return [
      {
        accessorKey: "year",
        header: t("result.cashFlowTable.column.year"),
        size: 10,
        Cell: ({ cell }) => renderFormattedCell(cell, false),
      },
      {
        accessorKey: "pvCost",
        header: t("result.cashFlowTable.column.pvCost"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "gensetCost",
        header: t("result.cashFlowTable.column.gensetCost"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "batteryCost",
        header: t("result.cashFlowTable.column.batteryCost"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "gridCost",
        header: t("result.cashFlowTable.column.gridCost"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "otherCosts",
        header: t("result.cashFlowTable.column.otherCosts"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "totalCapex",
        header: t("result.cashFlowTable.column.totalCapex"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "totalOpex",
        header: t("result.cashFlowTable.column.totalOpex"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "totalIncentive",
        header: t("result.cashFlowTable.column.totalIncentive"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "totalFuel",
        header: t("result.cashFlowTable.column.totalFuel"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "totalReplacement",
        header: t("result.cashFlowTable.column.totalReplacement"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "totalSalvage",
        header: t("result.cashFlowTable.column.totalSalvage"),
        size: 20,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
      {
        accessorKey: "rowTotal",
        header: t("common.totalcost", { defaultValue: "Total Cost" }),
        size: 140,
        minSize: 120,
        Cell: ({ cell }) => renderFormattedCell(cell),
      },
    ];
  }, [hasAssetCashflow, t, tableData]);

  const nominalTable = useMantineReactTable({
    data: tableData,
    columns,
    enableColumnFilters: true,
    enableColumnPinning: true,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enablePagination: false,
    initialState: {
      density: "xs",
      columnPinning: hasAssetCashflow
        ? { left: ["rowLabel"] }
        : { right: ["rowTotal"] },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Group gap="xs">
        <Button
          size="xs"
          variant="default"
          onClick={() => exportTableToCsv(table, isDiscounted ? "cashflow_discounted.csv" : "cashflow_nominal.csv")}
        >
          {t("common.exportCsv")}
        </Button>
      </Group>
    ),
    mantineTableContainerProps: {
      sx: { height: tableData?.length > 9 ? "auto" : "500px" },
    },
    // mantineTableHeadCellProps: {
    //   sx: {
    //     fontWeight: "normal",
    //     fontSize: "14px",
    //     fontFamily: "Nunito",
    //     borderTop: "1px solid #dee2e6 !important",
    //   },
    // },
    // mantineTableBodyProps: {
    //   sx: {
    //     fontWeight: "normal",
    //     fontSize: "14px",
    //     fontFamily: "Nunito",
    //     maxHeight: "1000px",
    //   },
    // },
    mantineTopToolbarProps: {
      sx: { backgroundColor },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor,
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: { backgroundColor },
    },
    mantineTableBodyProps: {
      sx: { backgroundColor },
    },
    mantineTableBodyCellProps: {
      sx: textCellSx,
    },
    mantineTableHeadCellProps: {
      sx: textCellSx,
    },
  });

  return <MantineReactTable table={nominalTable} />;
};

export default CashFlowTable;
